import { setUser } from '@/config/apm';
import { resolveBrand } from '@/config/brand';
import { eventBus } from '@/utils/eventBus';
/**
 * 认证工具类
 * 处理token的存储和获取
 */

// Token和Ticket存储的key
const TOKEN_KEY = 'anmo_ai_token';
const TICKET_KEY = 'anmo_ai_ticket';
const USER_INFO_KEY = 'anmo_ai_user_info';
const UTM_SOURCE_KEY = 'anmo_ai_utm_source';
// 团队空间切换时备份的个人 token（V0.1 团队协作：团队 token 失效时兜底回退）
const PERSONAL_TOKEN_KEY = 'anmo_ai_personal_token';

/**
 * 手机号脱敏处理
 * @param mobile 原始手机号
 * @returns 脱敏后的手机号 (如: 138****5678)
 */
export const maskMobile = (mobile: string): string => {
  if (!mobile || mobile.length < 7) {
    return mobile; // 如果手机号格式不正确，直接返回
  }

  // 中国手机号格式：保留前3位和后4位，中间用****代替
  if (mobile.length === 11) {
    return mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
  }

  // 其他格式：保留前3位和后2位
  return mobile.replace(/(\d{3})\d+(\d{2})/, '$1****$2');
};


/**
 * 获取 URL 中的 invite_code 参数
 * @returns invite_code 值 | null
 */
export const getInviteCode = (): string | null => {
  return new URLSearchParams(window.location.search).get('invite_code');
};

export const saveUtmSource = (): void => {
  const utm = new URLSearchParams(window.location.search).get('utm_source');
  if (utm) localStorage.setItem(UTM_SOURCE_KEY, utm);
};

export const getUtmSource = (): string | null => {
  return localStorage.getItem(UTM_SOURCE_KEY);
};

/**
 * 检查手机号是否已经脱敏
 * @param mobile 手机号
 * @returns 是否已脱敏
 */
const isMobileMasked = (mobile: string): boolean => {
  return !!(mobile && mobile.includes('****'));
};

/**
 * 存储token到localStorage
 * @param token 用户token
 */
export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * 获取localStorage中的token
 * @returns 存储的token或null
 */
export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 清除localStorage中的token
 */
export const clearToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PERSONAL_TOKEN_KEY);
};

/** 团队空间：备份个人 token（首次从个人切往团队时保存） */
export const setPersonalTokenBackup = (token: string): void => {
  localStorage.setItem(PERSONAL_TOKEN_KEY, token);
};

/** 读取备份的个人 token */
export const getPersonalTokenBackup = (): string | null => {
  return localStorage.getItem(PERSONAL_TOKEN_KEY);
};

/** 清除备份的个人 token（回退到个人空间后） */
export const clearPersonalTokenBackup = (): void => {
  localStorage.removeItem(PERSONAL_TOKEN_KEY);
};

/**
 * 检查是否有token
 * @returns 是否有token
 */
export const hasToken = (): boolean => {
  return !!getToken();
};

/**
 * 存储用户信息（保存完整信息，不进行脱敏处理）
 * @param userInfo 用户信息对象
 */
export const setUserInfo = (userInfo: any): void => {
  if (!userInfo) {
    localStorage.removeItem(USER_INFO_KEY);
    return;
  }

  // 直接保存完整信息，不进行脱敏处理
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  const userid = userInfo.id ? userInfo.id : userInfo.user_id;
  setUser(userid);
};

/**
 * 获取用户信息（包含完整信息）
 * @returns 用户信息或null
 */
export const getUserInfo = (): any | null => {
  const userInfo = localStorage.getItem(USER_INFO_KEY);
  return userInfo ? JSON.parse(userInfo) : null;
};

/**
 * 获取用户信息（用于显示，手机号脱敏）
 * @returns 脱敏后的用户信息或null
 */
export const getUserInfoForDisplay = (): any | null => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    return null;
  }

  // 创建副本并脱敏手机号
  const displayUserInfo = { ...userInfo };
  if (displayUserInfo.mobile && !isMobileMasked(displayUserInfo.mobile)) {
    displayUserInfo.mobile = maskMobile(displayUserInfo.mobile);
  }

  return displayUserInfo;
};

/**
 * 获取用户完整手机号
 * @returns 完整手机号或null
 */
export const getUserFullMobile = (): string | null => {
  const userInfo = getUserInfo();
  return userInfo?.mobile || null;
};

/**
 * 获取用户脱敏手机号
 * @returns 脱敏手机号或null
 */
export const getUserMaskedMobile = (): string | null => {
  const userInfo = getUserInfoForDisplay();
  return userInfo?.mobile || null;
};


/**
 * 校验是否链接过来且无用户信息
 */
export const isHandler = () => { 
    const hasInviteCode = !!getInviteCode();
    const hasUserInfo = !!getUserInfo();
    
    // 有邀请码 且 没有用户信息
    return hasInviteCode && !hasUserInfo;
};


/**
 * 获取海报码
 * @returns 
 */
export const getGiftId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const from = urlParams.get("from");
  
  if (!from) return null;
  
  // 解码 from 参数
  const decodedFrom = decodeURIComponent(from);
  
  // 提取 gift_id
  const giftIdMatch = decodedFrom.match(/gift_id=([^&]+)/);
  return giftIdMatch ? giftIdMatch[1] : null;
};


/**
 * 获取是否携带gift_id  代表着 海报扫码过来的
 */
export const isGift = () => { 
  const hasUserInfo = !!getUserInfo();
  const hasGiftId = !!getGiftId();
  //有海报码且没有用户信息
  return hasGiftId && !hasUserInfo;
};



/**
 * 获取用户显示名称
 * 优先显示用户名，其次显示脱敏手机号，最后显示默认名称
 * @param defaultName 默认显示名称
 * @returns 显示名称
 */
export const getUserDisplayName = (defaultName: string = '用户'): string => {
  const userInfo = getUserInfo();
  if (!userInfo) {
    return defaultName;
  }

  // 优先显示用户名
  if (userInfo.user_name || userInfo.username || userInfo.nickname) {
    return userInfo.user_name || userInfo.username || userInfo.nickname;
  }

  // 其次显示脱敏手机号
  if (userInfo.mobile) {
    return userInfo.mobile;
  }

  return defaultName;
};

/**
 * 清除用户信息
 */
export const clearUserInfo = (): void => {
  localStorage.removeItem(USER_INFO_KEY);
};

/**
 * 存储ticket到localStorage
 * @param ticket 用户ticket
 */
export const setTicket = (ticket: string): void => {
  localStorage.setItem(TICKET_KEY, ticket);
};

/**
 * 获取localStorage中的ticket
 * @returns 存储的ticket或null
 */
export const getTicket = (): string | null => {
  return localStorage.getItem(TICKET_KEY);
};

/**
 * 清除localStorage中的ticket
 */
export const clearTicket = (): void => {
  localStorage.removeItem(TICKET_KEY);
};

/**
 * 检查是否有ticket
 * @returns 是否有ticket
 */
export const hasTicket = (): boolean => {
  // 检查URL参数中的ticket
  const urlParams = new URLSearchParams(window.location.search);
  const ticketFromUrl = urlParams.get('ticket');

  // 检查localStorage中的ticket
  const ticketFromStorage = getTicket();

  // 如果URL中有ticket，保存到localStorage
  if (ticketFromUrl) {
    setTicket(ticketFromUrl);
    return true;
  }

  return !!ticketFromStorage;
};

/**
 * 清除所有认证数据
 */
export const clearAuthData = (): void => {
  clearToken();
  clearTicket();
  clearUserInfo();
};

/**
 * 清除所有用户数据（包括认证数据、Redux状态、本地存储等）
 * 用于用户退出登录或切换账号时的完整数据清理
 */
export const clearAllUserData = async (): Promise<void> => {
  try {
    console.log('[Auth] 开始清理所有用户数据...');

    // 1. 清除认证数据
    clearAuthData();

    // 2. 重置Redux状态并清除持久化数据
    try {
      const { persistor } = await import('@/store');
      const store = (await import('@/store')).default;

      // 重置所有slice状态
      const imageSlice = await import('@/store/slices/imageGenerationSlice');
      const videoSlice = await import('@/store/slices/videoGenerationSlice');
      const toolsSlice = await import('@/store/slices/toolsGenerationSlice');
      const generationSlice = await import('@/store/slices/generationSlice');

      store.dispatch(imageSlice.resetToInitialState());
      store.dispatch(videoSlice.resetVideoToInitialState());
      store.dispatch(toolsSlice.resetToInitialState());
      store.dispatch(generationSlice.resetGenerationToInitialState());

      // 空间状态一并归零（V0.1）：persistor.purge 只删存储不清内存，
      // 不 dispatch 的话登录前 rehydrate 出的团队空间会残留内存，
      // 重新登录后侧栏仍显示旧团队（且会被 redux-persist 重新写回 localStorage）
      const spaceSlice = await import('@/store/slices/spaceSlice');
      store.dispatch(spaceSlice.resetSpace());

      // 清除持久化数据
      await persistor.purge();
      console.log('[Auth] Redux状态已重置，持久化数据已清理');
    } catch (error) {
      console.error('[Auth] 清理Redux状态失败:', error);
    }

    // 3. 清除所有localStorage用户数据
    try {
      const { LocalStorageCleaner } = await import('@/utils/localStorageCleaner');
      LocalStorageCleaner.clearAllUserData({
        keepAuth: false, // 不保留认证信息
        verbose: true,
      });
    } catch (error) {
      console.error('[Auth] 清理localStorage失败:', error);
    }

    // 4. 清除API缓存
    try {
      const { apiCache } = await import('@/utils');
      apiCache.clear();
      console.log('[Auth] API缓存已清理');
    } catch (error) {
      console.error('[Auth] 清理API缓存失败:', error);
    }

    // 5. 清除跨页面会话
    try {
      const { CrossPageSessionManager } = await import('@/utils/CrossPageSessionManager');
      const sessionManager = CrossPageSessionManager.getInstance();
      sessionManager.clearSession();
      console.log('[Auth] 跨页面会话已清理');
    } catch (error) {
      console.error('[Auth] 清理跨页面会话失败:', error);
    }

    // 6. 清除其他可能的用户相关数据
    const additionalKeys = [
      'imageGenerationHistory',
      'videoGenerationHistory',
      'textGenerationHistory',
      'amai_user_preferences',
      'amai_chat_history',
      'amai_generation_settings',
      'amai_active_session_id',
      'creationActiveTab',
      'tools_generation_tasks',
      'tools_character_reference_image',
      'tools_face_replacement_data',
      'tools_face_replacement_image',
      'tools_mask_image',
      'mask_drawing_cache',
      'tools_outfit_change_data',
      'tools_outfit_reference_image',
      'tools_outfit_replacement_image',
      'tools_active_tool_tab',
      'tools_movie_replacement_image',
      'tools_movie_template_data',
      'user_token',
    ];

    additionalKeys.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`[Auth] 清理${key}失败:`, error);
      }
    });

    // 清除会话输入内容
    try {
      const keys = Object.keys(localStorage);
      const sessionInputKeys = keys.filter(key => key.startsWith('session_input_'));

      sessionInputKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn(`[Auth] 清理会话输入内容失败: ${key}`, error);
        }
      });

      if (sessionInputKeys.length > 0) {
        console.log(`[Auth] 已清除 ${sessionInputKeys.length} 个会话的输入内容`);
      }
    } catch (error) {
      console.error('[Auth] 清理会话输入内容失败:', error);
    }

    console.log('[Auth] ✅ 所有用户数据清理完成');
  } catch (error) {
    console.error('[Auth] 清理用户数据时发生错误:', error);
    throw error;
  }
};

// 登录跳转配置
interface LoginRedirectConfig {
  useInternalLogin: boolean;
  externalLoginUrl?: string;
}

/**
 * 检查URL是否为内部登录模式
 * @param url 要检查的URL
 * @returns 是否为内部登录模式
 */
const isInternalLoginPattern = (url: string): boolean => {
  try {
    const patterns = import.meta.env.VITE_INTERNAL_LOGIN_PATTERNS?.split(',') || [];
    return patterns.some((pattern: string) => {
      const trimmedPattern = pattern.trim();
      return url.includes(trimmedPattern);
    });
  } catch (error) {
    console.warn('[Auth] 解析内部登录模式配置失败:', error);
    return false;
  }
};

/**
 * 安全地比较两个URL的origin
 * @param url1 第一个URL
 * @param url2 第二个URL
 * @returns 是否为相同origin
 */
const isSameOrigin = (url1: string, url2: string): boolean => {
  try {
    const origin1 = new URL(url1).origin;
    const origin2 = new URL(url2).origin;
    return origin1 === origin2;
  } catch (error) {
    console.warn('[Auth] URL比较失败:', { url1, url2, error });
    return false;
  }
};

/**
 * 验证URL格式是否有效
 * @param url 要验证的URL
 * @returns 是否为有效URL
 */
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * 获取登录跳转配置
 * 根据环境变量和当前状态决定使用内部登录还是外部登录
 */
export const getLoginRedirectConfig = (): LoginRedirectConfig => {
  const externalLoginUrl = import.meta.env.VITE_LOGIN_URL;
  const forceInternalLogin = import.meta.env.VITE_USE_INTERNAL_LOGIN === 'true';
  const currentOrigin = window.location.origin;

  // 如果强制使用内部登录，直接返回内部登录配置
  if (forceInternalLogin) {
    console.log('[Auth] 强制使用内部登录 (VITE_USE_INTERNAL_LOGIN=true)');
    return {
      useInternalLogin: true,
      externalLoginUrl: undefined,
    };
  }

  // 如果没有配置外部登录URL，使用内部登录
  if (!externalLoginUrl) {
    console.log('[Auth] 未配置外部登录URL，使用内部登录');
    return {
      useInternalLogin: true,
      externalLoginUrl: undefined,
    };
  }

  // 验证外部登录URL格式
  if (!isValidUrl(externalLoginUrl)) {
    console.warn('[Auth] 配置了非法的外部登录URL:', externalLoginUrl);
    return {
      useInternalLogin: true,
      externalLoginUrl: undefined,
    };
  }

  // 如果外部登录URL和当前域名相同，使用内部登录
  if (isSameOrigin(externalLoginUrl, currentOrigin)) {
    console.log('[Auth] 外部登录URL与当前域名相同，使用内部登录');
    return {
      useInternalLogin: true,
      externalLoginUrl: undefined,
    };
  }

  // 检查是否匹配内部登录模式
  if (isInternalLoginPattern(externalLoginUrl)) {
    console.log('[Auth] 匹配内部登录模式，使用内部登录');
    return {
      useInternalLogin: true,
      externalLoginUrl: undefined,
    };
  }

  // 其他情况使用外部登录
  console.log('[Auth] 使用外部登录:', externalLoginUrl);
  return {
    useInternalLogin: false,
    externalLoginUrl,
  };
};

// React Router导航函数类型
type NavigateFunction = (to: any, options?: { replace?: boolean; state?: any }) => void;

/**
 * 统一的登录跳转函数（使用 window.location.href，不依赖 React Router）
 * 用于类组件或非 React Router 上下文中
 * @param from 来源页面路径，用于登录后跳转回来
 */
export const redirectToLoginWithoutNavigate = (from?: string): void => {
  // 品牌配置站内登录弹窗（如 gxzs 高新智算）：认证失效不跳转，弹 LoginModal 原地登录
  if (resolveBrand().useLoginModal) {
    eventBus.emit('showLoginModal');
    return;
  }

  const config = getLoginRedirectConfig();
  const currentPath = from || window.location.pathname + window.location.search;

  if (config.useInternalLogin) {
    // 使用内部登录页面
    console.log('[Auth] 跳转到内部登录页面');
    window.location.href = `/login?from=${encodeURIComponent(currentPath)}`;
  } else {
    // 使用外部登录页面
    console.log('[Auth] 跳转到外部登录页面:', config.externalLoginUrl);
    const currentUrl = currentPath || `${window.location.origin}${resolveBrand().defaultRoute || '/home'}`;
    const callback = encodeURIComponent(currentUrl);
    const loginUrl = `${config.externalLoginUrl}?callback=${callback}`;

    try {
      window.location.href = loginUrl;
    } catch (error) {
      console.error('[Auth] 跳转到外部登录页面失败:', error);
      // 降级到内部登录
      console.log('[Auth] 降级使用内部登录页面');
      window.location.href = `/login?from=${encodeURIComponent(currentUrl)}`;
    }
  }
};

/**
 * 清除用户数据并跳转到登录页（统一处理函数）
 * 用于错误处理场景，如错误边界、全局错误处理等
 * @param from 来源页面路径，用于登录后跳转回来
 */
export const clearDataAndRedirectToLogin = async (from?: string): Promise<void> => {
  try {
    // 清除用户数据
    await clearAllUserData();
    console.log('[Auth] 用户数据已清除，准备跳转到登录页');
    
    // 跳转到登录页
    redirectToLoginWithoutNavigate(from);
  } catch (err) {
    console.error('[Auth] 清除数据并跳转登录失败:', err);
    // 如果清除数据失败，至少跳转到登录页
    redirectToLoginWithoutNavigate(from);
  }
};


/**
 * 统一的登录跳转函数
 * @param navigate React Router的navigate函数
 * @param from 来源页面路径，用于登录后跳转回来
 */
export const redirectToLogin = (navigate?: NavigateFunction, from?: string) => {
  // 品牌配置站内登录弹窗（如 gxzs 高新智算）：认证失效不跳转，弹 LoginModal 原地登录
  if (resolveBrand().useLoginModal) {
    eventBus.emit('showLoginModal');
    return;
  }

  const config = getLoginRedirectConfig();

  if (config.useInternalLogin) {
    // 使用内部登录页面
    console.log('[Auth] 跳转到内部登录页面');
    const fromPath = from || window.location.pathname + window.location.search;
    
    if (navigate) {
      navigate('/login', {
        replace: true,
        state: { from: fromPath },
      });
    } else {
      // 如果没有传入 navigate，使用 window.location 进行跳转
      window.location.href = `/login${fromPath ? `?from=${encodeURIComponent(fromPath)}` : ''}`;
    }
  } else {
    // 使用外部登录页面
    console.log('[Auth] 跳转到外部登录页面:', config.externalLoginUrl);

    // 生成更完整的回调URL，包含当前完整路径
    const currentUrl = from || `${window.location.origin}${resolveBrand().defaultRoute || '/home'}`;
    const callback = encodeURIComponent(currentUrl);
    const loginUrl = `${config.externalLoginUrl}?callback=${callback}`;

    try {
      window.location.href = loginUrl;
    } catch (error) {
      console.error('[Auth] 跳转到外部登录页面失败:', error);
      // 降级到内部登录
      console.log('[Auth] 降级使用内部登录页面');
      if (navigate) {
        navigate('/login', {
          replace: true,
          state: { from: currentUrl },
        });
      } else {
        window.location.href = `/login?from=${encodeURIComponent(currentUrl)}`;
      }
    }
  }
};
